import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, type Book, type RatingsResponse } from "../api";
import { BookCover } from "../components/BookCover";
import { Button, Card, EmptyState, SectionHeading } from "../components/ui";
import { StarRating } from "../components/StarRating";

export default function LibraryPage() {
  const qc = useQueryClient();
  const [showDeleted, setShowDeleted] = useState(false);
  const [filter, setFilter] = useState<"all" | "unread" | "finished">("all");

  const list = useQuery({
    queryKey: ["books"],
    queryFn: () => api.get<{ books: Book[] }>("/books"),
  });

  const deleted = useQuery({
    queryKey: ["books", "deleted"],
    queryFn: () => api.get<{ books: Book[] }>("/books?onlyDeleted=1"),
    enabled: showDeleted,
  });

  const current = useQuery({
    queryKey: ["current-book"],
    queryFn: () => api.get<{ book: Book | null }>("/books/current"),
  });

  const setCurrent = useMutation({
    mutationFn: (id: number) => api.post(`/books/${id}/set-current`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["current-book"] });
      qc.invalidateQueries({ queryKey: ["books"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.del(`/books/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["books"] });
      qc.invalidateQueries({ queryKey: ["books", "deleted"] });
      qc.invalidateQueries({ queryKey: ["current-book"] });
    },
  });

  const restore = useMutation({
    mutationFn: (id: number) => api.post(`/books/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["books"] });
      qc.invalidateQueries({ queryKey: ["books", "deleted"] });
    },
  });

  const books = list.data?.books ?? [];
  const currentId = current.data?.book?.id;
  const visible = books.filter((b) => {
    if (filter === "finished") return !!b.finishedAt;
    if (filter === "unread") return !b.finishedAt;
    return true;
  });

  return (
    <div className="flex flex-col gap-10">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <SectionHeading
          eyebrow="Our reading life"
          title="The library"
          description="Every book the club has gathered around."
        />
        <div className="flex items-center gap-2">
          <Link to="/recommendations">
            <Button variant="ghost">Get recommendations</Button>
          </Link>
          <Link to="/add-book">
            <Button>Add a book</Button>
          </Link>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <FilterPill label="All" active={filter === "all"} onClick={() => setFilter("all")} />
        <FilterPill label="Unfinished" active={filter === "unread"} onClick={() => setFilter("unread")} />
        <FilterPill label="Finished" active={filter === "finished"} onClick={() => setFilter("finished")} />
        <span className="text-xs text-plum-mute ml-2">{visible.length} of {books.length}</span>
      </div>

      {books.length === 0 ? (
        <Card accent>
          <EmptyState
            title="The library is empty"
            description="Add your first book to start the collection."
            action={<Link to="/add-book"><Button>Add a book</Button></Link>}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-7">
          {visible.map((b) => (
            <BookTile
              key={b.id}
              book={b}
              isCurrent={b.id === currentId}
              onSetCurrent={() => setCurrent.mutate(b.id)}
              onRemove={() => {
                if (confirm(`Remove "${b.title}" from the library? (You can restore it from the "Removed" section.)`)) {
                  remove.mutate(b.id);
                }
              }}
            />
          ))}
        </div>
      )}

      <div className="mt-6 border-t border-cream-200 pt-6">
        <button
          onClick={() => setShowDeleted((v) => !v)}
          className="text-xs uppercase tracking-[0.18em] text-plum-mute hover:text-rose"
        >
          {showDeleted ? "Hide removed books" : "Show removed books"}
          {deleted.data?.books.length ? ` (${deleted.data.books.length})` : ""}
        </button>

        {showDeleted ? (
          <div className="mt-6">
            {(deleted.data?.books.length ?? 0) === 0 ? (
              <p className="text-plum-mute italic text-sm">Nothing in the trash.</p>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-7 opacity-70">
                {deleted.data!.books.map((b) => (
                  <div key={b.id} className="flex flex-col items-center text-center">
                    <BookCover url={b.coverUrl} title={b.title} author={b.author} size="lg" className="grayscale-[20%]" />
                    <div className="mt-3 px-1">
                      <h4 className="font-serif text-sm text-plum leading-tight line-clamp-2">{b.title}</h4>
                      <p className="text-plum-mute text-xs italic mt-0.5">{b.author}</p>
                      <button
                        onClick={() => restore.mutate(b.id)}
                        className="mt-2 text-[0.7rem] uppercase tracking-[0.14em] text-rose hover:text-rose-dark"
                      >
                        Restore
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function FilterPill({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={[
        "px-3 py-1 rounded-full text-xs uppercase tracking-[0.14em] transition-colors",
        active ? "bg-rose text-white" : "bg-cream-100 text-plum-soft hover:bg-cream-200",
      ].join(" ")}
    >
      {label}
    </button>
  );
}

function BookTile({
  book,
  isCurrent,
  onSetCurrent,
  onRemove,
}: {
  book: Book;
  isCurrent: boolean;
  onSetCurrent: () => void;
  onRemove: () => void;
}) {
  const ratings = useQuery({
    queryKey: ["ratings", book.id],
    queryFn: () => api.get<RatingsResponse>(`/ratings/${book.id}`),
    staleTime: 60_000,
  });

  return (
    <div className="flex flex-col items-center text-center group relative">
      <Link to={`/book/${book.id}`}>
        <BookCover url={book.coverUrl} title={book.title} author={book.author} size="lg" className="group-hover:-translate-y-1" />
      </Link>
      <div className="mt-3 px-1 w-full">
        <Link to={`/book/${book.id}`}>
          <h3 className="font-serif text-base text-plum leading-tight line-clamp-2 hover:text-rose">
            {book.title}
          </h3>
        </Link>
        <p className="text-plum-soft text-xs mt-1 italic">{book.author}</p>

        {ratings.data?.average ? (
          <div className="flex justify-center mt-2">
            <StarRating value={ratings.data.average} readOnly size={14} showCount={ratings.data.count} />
          </div>
        ) : null}

        <div className="mt-2 flex flex-col items-center gap-1">
          {isCurrent ? (
            <span className="text-[0.65rem] uppercase tracking-[0.18em] text-rose-dark font-medium">
              Current
            </span>
          ) : (
            <button
              onClick={onSetCurrent}
              className="text-[0.7rem] uppercase tracking-[0.14em] text-plum-mute hover:text-rose"
            >
              Set as current
            </button>
          )}
          <button
            onClick={onRemove}
            className="text-[0.7rem] uppercase tracking-[0.14em] text-plum-mute/70 hover:text-rose"
          >
            Remove
          </button>
        </div>
      </div>
    </div>
  );
}
